import { ConvertWithRatesFn } from "lib/frontend/lib/currency";
import { calculateOTEFromProposal } from "lib/shared/compensation/calculateOTEFromProposal";
import { annualizeCashValue } from "lib/shared/compensation/cash";
import {
  getInCycleSalaryBand,
  getInCycleSalaryIncomeFrequency,
  getInCycleEmploymentSubType,
  getAnnualizedProposedSalary,
} from "lib/shared/compensation/pay-frequency";
import { ProposalWithOwnerAndRange } from "lib/shared/proposals/types";
import { Review, Company, SalaryBenchmarkType, SalaryIncomeFrequency } from "prisma/cm/client";
import { BENCHMARK_THRESHOLD_MID } from "utils/constants";
import {
  getDoesCompanyIncludeBonusTargetInOTECalculation,
  cellFormatterCurrency,
  cellFormatterCurrencyBenchmark,
  getDoesRangeIncludeBonusTargetInCompaRatioCalculation,
} from "utils/helpers";

export const calculateProposalCompaRatio = ({
  proposal,
  review,
  company,
  convert,
}: {
  proposal: ProposalWithOwnerAndRange;
  review: Review;
  company: Company;
  convert: ConvertWithRatesFn;
}) => {
  const isOTE = review.salaryBenchmarkType === SalaryBenchmarkType.TOTAL_PAY;
  let includeBonusTarget = getDoesCompanyIncludeBonusTargetInOTECalculation(company);

  const range = proposal.range ?? proposal.owner.range;

  if (range && !getDoesRangeIncludeBonusTargetInCompaRatioCalculation(range)) {
    // range-level setting for bonus target overrides company-level setting
    includeBonusTarget = false;
  }

  const after = isOTE
    ? //@ts-expect-error TS2345
      calculateOTEFromProposal(proposal, includeBonusTarget)
    : //@ts-expect-error TS2345
      getAnnualizedProposedSalary(proposal);

  const rangeIncomeFrequency = proposal.range
    ? proposal.range?.salaryIncomeFrequency
    : proposal.owner?.range?.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;

  const existingSalaryIncomeFrequency =
    proposal.owner.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;

  const proposedSalaryIncomeFrequency = getInCycleSalaryIncomeFrequency({
    proposalWithOwner: proposal,
  });

  const proposedEmploymentSubType = getInCycleEmploymentSubType({ proposalWithOwner: proposal });

  const requiresFrequencyConversion =
    proposedSalaryIncomeFrequency !== existingSalaryIncomeFrequency;

  const rangeMid = getInCycleSalaryBand({
    review,
    proposal,
    threshold: BENCHMARK_THRESHOLD_MID,
  });

  //@ts-expect-error TS2345
  const convertedAfter = cellFormatterCurrency(convert, proposal, after).raw;
  //@ts-expect-error TS2345
  const convertedMid = cellFormatterCurrencyBenchmark(convert, proposal, rangeMid).raw;

  const convertedMidAnnualized = annualizeCashValue({
    value: convertedMid,
    incomeFrequency: requiresFrequencyConversion
      ? proposedSalaryIncomeFrequency
      : rangeIncomeFrequency,
    employmentSubType: proposedEmploymentSubType,
  });

  // ratio of value to market
  return convertedAfter / convertedMidAnnualized;
};
